import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL from './config';
import './PeopleTab.css';
import publicOrigin from "./publicOrigin";
import FacePhoto, { FaceAvatar } from './FacePhoto';

// navigator.clipboard is missing outside secure contexts (and in some in-app
// browsers), so fall back to the old textarea + execCommand trick.
async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("copy failed");
}

// People are fetched a page at a time — presigning every cover URL for a big
// event in one request is slow, and the section starts collapsed anyway.
const PAGE_SIZE = 24;

const PeopleTab = ({ eventId }) => {
  const [clusters, setClusters] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState('idle');
  const [modalCluster, setModalCluster] = useState(null); // person modal open
  const [clusterPhotos, setClusterPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [toast, setToast] = useState('');
  // Which person's share sheet is open (with its resolved link), and whether it was just copied
  const [shareSheet, setShareSheet] = useState(null); // { cluster, url }
  const [shareCopied, setShareCopied] = useState(false);
  const pollRef = useRef(null);
  const editInputRef = useRef(null);
  const shareTokenRef = useRef(null);
  const sentinelRef = useRef(null);
  // Enter/Escape already handled the edit — makes the input's blur a no-op,
  // so Enter doesn't save twice and Escape doesn't save at all.
  const editDoneRef = useRef(false);

  useEffect(() => {
    fetchClusters();
    // Null the ref as well as clearing — startPolling treats a non-null ref as
    // "already polling", so a stale id would block polling forever when the
    // event changes mid-analysis.
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [eventId]);

  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  // Infinite scroll: when the sentinel under the grid comes into view, pull
  // the next page. The "Load more" button stays as a fallback for browsers
  // without IntersectionObserver (and for keyboard users).
  useEffect(() => {
    if (!expanded || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  });

  // Close modal on Escape; lightbox arrow keys
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (shareSheet) { setShareSheet(null); return; }
        if (lightboxIndex !== null) { setLightboxIndex(null); return; }
        if (modalCluster) { closeModal(); return; }
      }
      if (lightboxIndex !== null) {
        if (e.key === 'ArrowLeft') stepLightbox(-1);
        if (e.key === 'ArrowRight') stepLightbox(1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, clusterPhotos, modalCluster, shareSheet]);

  const token = () => localStorage.getItem('token');

  // offset 0 (re)loads the first page; a non-zero offset appends the next one.
  const fetchClusters = async (offset = 0) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/people?limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const page = data.clusters || [];
      setClusters(prev => {
        if (offset === 0) return page;
        // Guard against a double-append if two loads race.
        const seen = new Set(prev.map(c => c.id));
        return [...prev, ...page.filter(c => !seen.has(c.id))];
      });
      // Older server responses have no `total`; an empty later page means the
      // list shrank (e.g. re-analyzed) — stop asking for more either way.
      setTotal(prevTotal => {
        if (typeof data.total === 'number') return data.total;
        return offset === 0 ? page.length : (page.length === 0 ? offset : prevTotal);
      });
      setStatus(data.status || 'idle');
      if (data.status === 'running') startPolling();
    } catch (e) {
      console.error('PeopleTab fetchClusters:', e);
    }
  };

  const hasMore = clusters.length < total;

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchClusters(clusters.length);
    setLoadingMore(false);
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/people/status`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) return;
        const { status: s } = await res.json();
        if (s !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await fetchClusters();
        }
      } catch (e) { /* ignore */ }
    }, 2500);
  };

  const handleAnalyze = async () => {
    setStatus('running');
    try {
      await fetch(`${API_BASE_URL}/api/events/${eventId}/people/cluster`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      startPolling();
    } catch (e) {
      setStatus('idle');
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  // The event's share link/token is idempotent server-side (POST returns the
  // existing token if one was already generated) — safe to call even if the
  // studio never opened the Share modal on this event before.
  const getShareToken = async () => {
    if (shareTokenRef.current) return shareTokenRef.current;
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/share`, { method: 'POST' });
      const data = await res.json();
      shareTokenRef.current = data.token;
      return data.token;
    } catch (e) {
      return null;
    }
  };

  const personShareTitle = (cluster) =>
    cluster.label ? `${cluster.photo_count} photos with ${cluster.label}` : `${cluster.photo_count} photos`;

  // What the studio is actually handing over — used as the sheet's heading so
  // "share" reads as "send this person their own gallery page", not "share
  // this thumbnail".
  const personGalleryName = (cluster) =>
    cluster.label ? `${cluster.label}'s gallery` : "This person's gallery";

  const whatsappText = (cluster, url) =>
    cluster.label
      ? `Here are ${cluster.label}'s photos (${cluster.photo_count}) — ${url}`
      : `Here are your photos (${cluster.photo_count}) — ${url}`;

  // The share icon opens a small sheet so "Copy link" is always reachable —
  // the native share sheet doesn't offer it on every platform.
  const handleSharePerson = async (cluster, e) => {
    e.stopPropagation();
    const shareToken = await getShareToken();
    if (!shareToken) { showToast('Could not create share link'); return; }
    setShareCopied(false);
    setShareSheet({ cluster, url: `${publicOrigin()}/shared/${shareToken}/person/${cluster.id}` });
  };

  const handleCopyPersonLink = async () => {
    if (!shareSheet) return;
    try {
      await copyText(shareSheet.url);
      setShareCopied(true);
      showToast('Link copied to clipboard!');
      setTimeout(() => { setShareSheet(null); setShareCopied(false); }, 900);
    } catch (err) {
      showToast('Could not copy link');
    }
  };

  const handlePreviewGallery = () => {
    if (!shareSheet) return;
    window.open(shareSheet.url, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppShare = () => {
    if (!shareSheet) return;
    const text = encodeURIComponent(whatsappText(shareSheet.cluster, shareSheet.url));
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  const handleNativeSharePerson = async () => {
    if (!shareSheet) return;
    try {
      await navigator.share({ title: personShareTitle(shareSheet.cluster), url: shareSheet.url });
      setShareSheet(null);
    } catch (err) { /* user cancelled */ }
  };

  const openModal = async (cluster) => {
    setModalCluster(cluster);
    setClusterPhotos([]);
    setLightboxIndex(null);
    setLoadingPhotos(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/people/${cluster.id}/photos`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      const data = await res.json();
      setClusterPhotos(data.photos || []);
    } catch (e) {
      console.error('PeopleTab openModal:', e);
    }
    setLoadingPhotos(false);
  };

  const closeModal = () => {
    setModalCluster(null);
    setClusterPhotos([]);
    setLightboxIndex(null);
  };

  const handleStartEdit = (cluster, e) => {
    e.stopPropagation();
    editDoneRef.current = false;
    setEditingId(cluster.id);
    setEditValue(cluster.label || '');
  };

  const handleSaveEdit = async (clusterId) => {
    const trimmed = editValue.trim();
    setEditingId(null);
    setEditValue('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/people/${clusterId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: trimmed || null }),
      });
      if (!res.ok) throw new Error(`rename failed (${res.status})`);
      const updated = (prev) => prev.map(c => c.id === clusterId ? { ...c, label: trimmed || null } : c);
      setClusters(updated);
      if (modalCluster?.id === clusterId) setModalCluster(prev => ({ ...prev, label: trimmed || null }));
    } catch (e) {
      console.error('PeopleTab rename:', e);
      showToast('Could not save the name — please try again');
    }
  };

  const handleEditBlur = (clusterId) => {
    if (editDoneRef.current) return;
    editDoneRef.current = true;
    handleSaveEdit(clusterId);
  };

  const handleEditKey = (e, clusterId) => {
    // Keep Escape from also closing the person modal, and Enter from
    // triggering any outer handlers.
    e.stopPropagation();
    if (e.key === 'Enter') {
      editDoneRef.current = true;
      handleSaveEdit(clusterId);
    }
    if (e.key === 'Escape') {
      editDoneRef.current = true;
      setEditingId(null);
      setEditValue('');
    }
  };

  const stepLightbox = (dir) => {
    setLightboxIndex(prev => (prev + dir + clusterPhotos.length) % clusterPhotos.length);
  };

  // ── Idle ──
  if (status === 'idle' && clusters.length === 0) {
    return (
      <div className="pt-empty">
        <div className="pt-empty-icon">
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="8" stroke="currentColor" strokeWidth="2.5" fill="none" />
            <circle cx="32" cy="16" r="8" stroke="currentColor" strokeWidth="2.5" fill="none" />
            <path d="M4 40c0-6.627 5.373-12 12-12h16c6.627 0 12 5.373 12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          </svg>
        </div>
        <h3 className="pt-empty-title">Find People in Your Photos</h3>
        <p className="pt-empty-desc">
          Photentra scans every face in this event and groups photos by person — automatically.
          No one needs to upload a selfie.
        </p>
        <button className="pt-analyze-btn" onClick={handleAnalyze}>Analyze Faces</button>
      </div>
    );
  }

  // ── Running ──
  if (status === 'running') {
    return (
      <div className="pt-empty">
        <div className="pt-spinner" aria-label="Analyzing" />
        <h3 className="pt-empty-title">Analyzing faces…</h3>
        <p className="pt-empty-desc">This usually takes under a minute. You can leave this page — it'll be ready when you come back.</p>
      </div>
    );
  }

  const lightboxPhoto = lightboxIndex !== null ? clusterPhotos[lightboxIndex] : null;

  // ── Results ──
  return (
    <div className="pt-root">
      <div className="pt-toolbar">
        <button
          className="pt-section-toggle"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
        >
          <span className="pt-toolbar-count">{total} {total === 1 ? 'person' : 'people'} found</span>
          <svg
            className={`pt-toggle-chevron${expanded ? ' open' : ''}`}
            viewBox="0 0 16 16" fill="none" width="14" height="14" aria-hidden="true"
          >
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="pt-toggle-hint">{expanded ? 'Collapse' : 'Expand'}</span>
        </button>
        <button className="pt-rerun-btn" onClick={handleAnalyze}>↺ Re-analyze</button>
      </div>

      {/* Teaches the concept once, before anyone hunts for what the share icon
          on a card actually does. */}
      {expanded && (
        <p className="pt-section-hint">
          Each person has their own gallery page — share the link and they land on a
          page of just their photos.
        </p>
      )}

      {expanded && (
      <div className="pt-grid">
        {clusters.map(cluster => (
          <div
            key={cluster.id}
            className="pt-card"
            onClick={() => openModal(cluster)}
          >
            <div className="pt-card-photo">
              {cluster.cover_url
                ? <FacePhoto src={cluster.cover_url} box={cluster.cover_face_box} showBox />
                : <div className="pt-card-placeholder">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                    </svg>
                  </div>
              }
              <span className="pt-card-count">{cluster.photo_count}</span>
              {/* Labeled on hover: a bare share glyph on a photo tile reads as
                  "share this picture", not "send this person their gallery". */}
              <button
                className="pt-card-share-btn"
                onClick={(e) => handleSharePerson(cluster, e)}
                title={`Share ${cluster.label ? `${cluster.label}'s` : "this person's"} gallery page`}
                aria-label={`Share ${cluster.label ? `${cluster.label}'s` : "this person's"} gallery page`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.367A2.52 2.52 0 0 1 13 4.5Z"/></svg>
                <span className="pt-card-share-label">Share gallery</span>
              </button>
            </div>
            <div className="pt-card-label-row">
              {editingId === cluster.id ? (
                <input
                  ref={editInputRef}
                  className="pt-label-input"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={() => handleEditBlur(cluster.id)}
                  onKeyDown={e => handleEditKey(e, cluster.id)}
                  onClick={e => e.stopPropagation()}
                  placeholder="Add name"
                  maxLength={50}
                />
              ) : (
                <>
                  <span className="pt-card-name">{cluster.label || <em>Unknown</em>}</span>
                  <button
                    className="pt-edit-btn"
                    onClick={e => handleStartEdit(cluster, e)}
                    title="Rename"
                    aria-label="Rename person"
                  >
                    <svg viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 0 0-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 0 0 0-.354l-1.086-1.086ZM11.189 6.25 9.75 4.81 3.198 11.36a.25.25 0 0 0-.064.108l-.65 2.278 2.277-.65a.25.25 0 0 0 .108-.065L11.19 6.25z" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      )}

      {expanded && hasMore && (
        <div className="pt-load-more-row">
          <div ref={sentinelRef} className="pt-scroll-sentinel" aria-hidden="true" />
          <button className="pt-load-more-btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Load more (${total - clusters.length} left)`}
          </button>
        </div>
      )}

      {/* ── Person modal ── */}
      {modalCluster && (
        <div className="pt-modal-overlay" onClick={closeModal}>
          <div className="pt-modal" onClick={e => e.stopPropagation()}>
            <div className="pt-modal-header">
              <div className="pt-modal-who">
                {modalCluster.cover_url && (
                  <FaceAvatar
                    src={modalCluster.cover_url}
                    box={modalCluster.cover_face_box}
                    className="pt-modal-avatar"
                  />
                )}
                <div>
                  <h3 className="pt-modal-name">{modalCluster.label || 'Unknown'}</h3>
                  <span className="pt-modal-sub">{modalCluster.photo_count} photos</span>
                </div>
              </div>
              <button className="pt-modal-close" onClick={closeModal} aria-label="Close">✕</button>
            </div>

            <div className="pt-modal-body">
              {loadingPhotos ? (
                <div className="pt-modal-loading"><div className="pt-spinner" /></div>
              ) : clusterPhotos.length === 0 ? (
                <p className="pt-modal-empty">No photos found.</p>
              ) : (
                <div className="pt-photo-grid">
                  {clusterPhotos.map((photo, i) => (
                    <div
                      key={photo.id}
                      className="pt-photo-tile"
                      onClick={() => setLightboxIndex(i)}
                    >
                      <FacePhoto
                        src={photo.thumbnail_url}
                        box={photo.face_box}
                        showBox
                        imgProps={{ loading: 'lazy' }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightboxPhoto && (
        <div className="pt-lightbox" onClick={() => setLightboxIndex(null)}>
          <button className="pt-lb-nav pt-lb-nav--prev" onClick={e => { e.stopPropagation(); stepLightbox(-1); }}>‹</button>
          <img
            className="pt-lb-img"
            src={lightboxPhoto.image_url || lightboxPhoto.thumbnail_url}
            alt=""
            onClick={e => e.stopPropagation()}
            draggable={false}
          />
          <button className="pt-lb-nav pt-lb-nav--next" onClick={e => { e.stopPropagation(); stepLightbox(1); }}>›</button>
          <span className="pt-lb-counter">{lightboxIndex + 1} / {clusterPhotos.length}</span>
        </div>
      )}

      {/* ── Share sheet (Copy link / native share) ── */}
      {shareSheet && (
        <div className="pt-share-overlay" onClick={() => setShareSheet(null)}>
          <div className="pt-share-sheet" onClick={e => e.stopPropagation()}>
            <div className="pt-share-header">
              {shareSheet.cluster.cover_url && (
                <FaceAvatar
                  src={shareSheet.cluster.cover_url}
                  box={shareSheet.cluster.cover_face_box}
                  className="pt-share-avatar"
                />
              )}
              <span className="pt-share-title">{personGalleryName(shareSheet.cluster)}</span>
              <span className="pt-share-sub">
                A personal page with the {shareSheet.cluster.photo_count}{' '}
                {shareSheet.cluster.photo_count === 1 ? 'photo' : 'photos'} they appear in
              </span>
            </div>

            {/* The person page is reached through the event's share link and
                footer-links back to the full gallery, so a studio deciding who
                to send this to needs to know it is not private to one guest. */}
            <p className="pt-share-note">
              Anyone with this link can view and download these photos, and can also
              open the full event gallery.
            </p>

            <div className="pt-share-url" title={shareSheet.url}>{shareSheet.url}</div>

            <button
              className={`pt-share-option primary${shareCopied ? ' copied' : ''}`}
              onClick={handleCopyPersonLink}
            >
              {shareCopied ? '✅ Copied!' : '🔗 Copy link'}
            </button>

            <button className="pt-share-option" onClick={handleWhatsAppShare}>
              💬 Send on WhatsApp
            </button>

            {typeof navigator !== 'undefined' && navigator.share && (
              <button className="pt-share-option" onClick={handleNativeSharePerson}>
                📤 Share via…
              </button>
            )}

            {/* Lets the studio see exactly what the guest will get before sending. */}
            <button className="pt-share-preview" onClick={handlePreviewGallery}>
              👁 Preview this gallery
            </button>

            <button className="pt-share-cancel" onClick={() => setShareSheet(null)}>Cancel</button>
          </div>
        </div>
      )}

      {toast && <div className="pt-toast">{toast}</div>}
    </div>
  );
};

export default PeopleTab;
